import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
  IonPage,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonList,
  IonItem,
  IonLabel,
  IonIcon,
  IonBadge,
  IonText,
  IonGrid,
  IonRow,
  IonCol,
  IonButton,
  IonModal,
  IonRefresherContent,
  useIonRouter
} from '@ionic/react';
import { list, barChart, closeCircle, person, checkmarkCircle, alertCircle, search, chevronBack } from 'ionicons/icons';
import styles from './Leads.module.scss';
import LeadsListModal from '../components/LeadsListModal';
import BasePageWithHeader, { TitleCard } from '../components/BasePageWithHeader';
import PositionedRefresher from '../components/PositionedRefresher';
import SearchModal from '../components/SearchModal';
import { useTranslation } from 'react-i18next';

const Leads: React.FC = () => {
  const { t } = useTranslation();
  const router = useIonRouter();
  const [showLeadsListModal, setShowLeadsListModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);

  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
  const titleCardElementRef = useRef<HTMLDivElement>(null);
  const contentElementRef = useRef<HTMLIonGridElement>(null);
  const wasAtTop = useRef(true);
  const [refresherDisabled, setRefresherDisabled] = useState(false);

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleScroll = useScrollSnap(contentElementRef, 66, applyProgress);

  // Mock data for demonstration
  const leadsStats = {
    total: 24,
    new: 8,
    contacted: 12,
    qualified: 4,
    conversionRate: 16.7
  };

  const handleClose = () => {
    if (router.canGoBack()) {
      router.goBack();
    } else {
      router.push('/tabs/dashboard');
    }
  };

  // Debug logging
  useEffect(() => {
    console.log('Modal state - LeadsList:', showLeadsListModal);
  }, [showLeadsListModal]);

  return (
    <IonPage>
      <BasePageWithHeader
        ref={baseHeaderRef}
        title={t('pages.Leads.leadsOverview')}
        toolbarClassName={styles.toolbar}
        smallTitleClassName={styles.smallTitle}
        titleHeaderClassName={styles.titleHeader}
        startButtons={
          <IonButton onClick={handleClose} shape='round' aria-label={t('pages.Leads.closeLeads')}>
            <IonIcon icon={chevronBack} color='primary' />
          </IonButton>
        }
        endButtons={
          <IonButton shape='round' onClick={() => setShowSearchModal(true)} className={styles.searchButton} aria-label={t('pages.Leads.searchLeads')}>
            <IonIcon icon={search} />
          </IonButton>
        }
      >
        <IonContent scrollY={false}>
          <PositionedRefresher
            titleRef={titleCardElementRef}
            contentRef={contentElementRef}
            offset={0}
            anchor="bottom"
            id="leads-refresher"
            pullMin={120}
            pullMax={200}
            pullFactor={0.6}
            disabled={refresherDisabled}
            onIonRefresh={(e) => {
              e.detail.complete();
            }}
            style={{
              '--ion-color-step-250': 'var(--ion-background-color)',
              '--ion-color-step-200': 'var(--ion-background-color)',
              '--ion-background-color': 'transparent',
            } as React.CSSProperties}
          >
            <IonRefresherContent pullingIcon="bubbles" refreshingSpinner="bubbles" />
          </PositionedRefresher>
          <IonGrid
            ref={contentElementRef}
            className={styles.leadsContainer}
            onScroll={(e) => {
              const top = (e.target as HTMLElement).scrollTop || 0;
              const atTop = top <= 5;
              if (atTop !== wasAtTop.current) {
                wasAtTop.current = atTop;
                setRefresherDisabled(!atTop);
              }
              handleScroll(top);
            }}
          >
            <div ref={titleCardElementRef}>
              <TitleCard
                ref={titleCardAnimationRef}
                title={t('pages.Leads.leadsOverview')}
                cardClassName={styles.leadsOverviewCard}
                titleContainerClassName={styles.titleContainer}
                titleClassName={styles.leadsTitle}
              >
                <IonGrid>
                  <IonRow className="ion-align-items-center">
                    <IonCol size="auto">
                      <div className={styles.scoreCircle}>
                        <IonIcon icon={person} className={styles.scoreIcon} />
                      </div>
                    </IonCol>
                    <IonCol>
                      <IonText>
                        <h1 className={styles.scoreValue}>{leadsStats.total}</h1>
                        <p className={styles.scoreDescription}>{t('pages.Leads.totalActiveLeads')}
                        </p>
                      </IonText>
                    </IonCol>
                  </IonRow>
                </IonGrid>

                {/* Leads Status Checklist */}
                <div className={styles.leadsChecklist}>
                  <div className={styles.checklistItem}>
                    <IonIcon
                      icon={alertCircle}
                      color="warning"
                      className={styles.checklistIcon}
                    />
                    <IonText>
                      <p className={styles.checklistText}>{t('pages.Leads.newLeads', { new: leadsStats.new })}</p>
                    </IonText>
                  </div>
                  <div className={styles.checklistItem}>
                    <IonIcon
                      icon={checkmarkCircle}
                      color="success"
                      className={styles.checklistIcon}
                    />
                    <IonText>
                      <p className={styles.checklistText}>{t('pages.Leads.contacted', { contacted: leadsStats.contacted })}</p>
                    </IonText>
                  </div>
                  <div className={styles.checklistItem}>
                    <IonIcon
                      icon={checkmarkCircle}
                      color="success"
                      className={styles.checklistIcon}
                    />
                    <IonText>
                      <p className={styles.checklistText}>{t('pages.Leads.conversionRate', { conversionRate: leadsStats.conversionRate })}</p>
                    </IonText>
                  </div>
                </div>
              </TitleCard>
            </div>

            {/* Leads List */}
            <IonCard className={styles.sectionCard}>
              <IonCardHeader>
                <IonCardTitle className={styles.sectionTitle}>
                  <IonIcon icon={list} className={styles.sectionIcon} />{t('pages.Leads.leadsList')}
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonText color="medium">
                  <p className={styles.sectionDescription}>{t('pages.Leads.viewAndManageAllYourLeads')}
                  </p>
                </IonText>
                <IonList className={styles.settingsList}>
                  <IonItem
                    button
                    detail
                    onClick={() => {
                      console.log('Leads list clicked');
                      setShowLeadsListModal(true);
                    }}
                    className={styles.settingsItem}
                  >
                    <IonIcon icon={list} slot="start" />
                    <IonLabel>
                      <h3>{t('pages.Leads.viewAllLeads')}</h3>
                      <p>{t('pages.Leads.searchFilterAndManageLeads')}</p>
                    </IonLabel>
                    <IonBadge slot="end" color="primary">{leadsStats.total}</IonBadge>
                  </IonItem>
                </IonList>
              </IonCardContent>
            </IonCard>

            {/* Lead Sources */}
            <IonCard className={styles.sectionCard}>
              <IonCardHeader>
                <IonCardTitle className={styles.sectionTitle}>
                  <IonIcon icon={person} className={styles.sectionIcon} />{t('pages.Leads.leadSources')}
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonText color="medium">
                  <p className={styles.sectionDescription}>{t('pages.Leads.quickAccessToLeadsFromDifferent')}
                  </p>
                </IonText>
                <IonList className={styles.settingsList}>
                  <IonItem
                    button
                    detail
                    onClick={() => console.log('Website leads clicked')}
                    className={styles.settingsItem}
                  >
                    <IonIcon icon={list} slot="start" />
                    <IonLabel>
                      <h3>{t('pages.Leads.myWebsite')}</h3>
                      <p>{t('pages.Leads.leadsFromYourWebsite')}</p>
                    </IonLabel>
                    <IonBadge slot="end" color="primary">12</IonBadge>
                  </IonItem>

                  <IonItem
                    button
                    detail
                    onClick={() => console.log('Social media leads clicked')}
                    className={styles.settingsItem}
                  >
                    <IonIcon icon={person} slot="start" />
                    <IonLabel>
                      <h3>{t('pages.Leads.socialMedia')}</h3>
                      <p>{t('pages.Leads.facebookInstagramTwitterLeads')}</p>
                    </IonLabel>
                    <IonBadge slot="end" color="secondary">8</IonBadge>
                  </IonItem>

                  <IonItem
                    button
                    detail
                    onClick={() => console.log('Google leads clicked')}
                    className={styles.settingsItem}
                  >
                    <IonIcon icon={barChart} slot="start" />
                    <IonLabel>
                      <h3>{t('pages.Leads.google')}</h3>
                      <p>{t('pages.Leads.googleAdsAndSearchLeads')}</p>
                    </IonLabel>
                    <IonBadge slot="end" color="tertiary">6</IonBadge>
                  </IonItem>

                  <IonItem
                    button
                    detail
                    onClick={() => console.log('Microsoft leads clicked')}
                    className={styles.settingsItem}
                  >
                    <IonIcon icon={checkmarkCircle} slot="start" />
                    <IonLabel>
                      <h3>{t('pages.Leads.microsoft')}</h3>
                      <p>{t('pages.Leads.bingAdsAndMicrosoftLeads')}</p>
                    </IonLabel>
                    <IonBadge slot="end" color="success">4</IonBadge>
                  </IonItem>

                  <IonItem
                    button
                    detail
                    onClick={() => console.log('Referral leads clicked')}
                    className={styles.settingsItem}
                  >
                    <IonIcon icon={alertCircle} slot="start" />
                    <IonLabel>
                      <h3>{t('pages.Leads.referral')}</h3>
                      <p>{t('pages.Leads.customerReferrals')}</p>
                    </IonLabel>
                    <IonBadge slot="end" color="warning">3</IonBadge>
                  </IonItem>

                  <IonItem
                    button
                    detail
                    onClick={() => console.log('Other leads clicked')}
                    className={styles.settingsItem}
                  >
                    <IonIcon icon={closeCircle} slot="start" />
                    <IonLabel>
                      <h3>{t('pages.Leads.other')}</h3>
                      <p>{t('pages.Leads.miscellaneousSources')}</p>
                    </IonLabel>
                    <IonBadge slot="end" color="medium">2</IonBadge>
                  </IonItem>
                </IonList>
              </IonCardContent>
            </IonCard>

            {/* Leads Analytics */}
            <IonCard className={styles.sectionCard}>
              <IonCardHeader>
                <IonCardTitle className={styles.sectionTitle}>
                  <IonIcon icon={barChart} className={styles.sectionIcon} />{t('pages.Leads.leadsAnalytics')}
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonText color="medium">
                  <p className={styles.sectionDescription}>{t('pages.Leads.viewDetailedAnalyticsAndInsightsAbout')}
                  </p>
                </IonText>
                <IonList className={styles.settingsList}>
                  <IonItem
                    button
                    detail
                    className={styles.settingsItem}
                  >
                    <IonIcon icon={barChart} slot="start" />
                    <IonLabel>
                      <h3>{t('pages.Leads.viewAnalytics')}</h3>
                      <p>{t('pages.Leads.conversionRatesTrendsAndPerformanceMetri')}</p>
                    </IonLabel>
                  </IonItem>
                </IonList>
              </IonCardContent>
            </IonCard>
          </IonGrid>
        </IonContent>
      </BasePageWithHeader>

      {/* Modals */}
      <IonModal
        isOpen={showLeadsListModal}
        onDidDismiss={() => {
          console.log('Leads list modal dismissed');
          setShowLeadsListModal(false);
        }}
        onWillDismiss={() => setShowLeadsListModal(false)}
        canDismiss={true}
        backdropDismiss={true}
        keepContentsMounted={false}
        handle={false}
      >
        <LeadsListModal onClose={() => setShowLeadsListModal(false)} />
      </IonModal>

      <SearchModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
      />
    </IonPage>
  );
};

export default Leads;